import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { COMPANY_CONFIG } from '../config/constants';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { name: 'Home', id: 'home' },
    { name: 'About', id: 'about' },
    { name: 'Products', id: 'products' },
    { name: 'Industries', id: 'industries' },
    { name: 'Process', id: 'process' },
    { name: 'Contact', id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/85 backdrop-blur-lg shadow-[0_4px_30px_rgba(0,0,0,0.05)] border-b border-gray-200/50 py-1' : 'bg-white/60 backdrop-blur-md border-b border-white/20 py-2'
        }`}
    >
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4 md:gap-6">
            <a href={`tel:${COMPANY_CONFIG.phoneFormatted}`} className="flex items-center gap-2 hover:text-blue-200 transition-colors">
              <Phone size={14} />
              <span className="hidden sm:inline">{COMPANY_CONFIG.phoneFormatted}</span>
            </a>
            <a href={`mailto:${COMPANY_CONFIG.email}`} className="flex items-center gap-2 hover:text-blue-200 transition-colors">
              <Mail size={14} />
              <span className="hidden sm:inline">{COMPANY_CONFIG.email}</span>
            </a>
          </div>
          <div className="text-xs hidden md:block">
            <span className="font-semibold">ISO 9001:2015 Certified</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="/lumentec-logo.png"
              alt="Lumentec Systems Logo"
              loading="eager"
              width="56"
              height="56"
              className="h-14 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-800 hover:text-blue-700 font-semibold transition-all duration-300 relative group py-1"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-300 group-hover:w-full opacity-0 group-hover:opacity-100 rounded-full"></span>
              </button>
            ))}
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`https://wa.me/${COMPANY_CONFIG.whatsappNumber}?text=${encodeURIComponent(COMPANY_CONFIG.whatsappDefaultMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 text-green-600 hover:text-white hover:bg-green-500 border-2 border-green-500 rounded-xl font-bold transition-all duration-500 hover:shadow-[0_8px_20px_rgba(34,197,94,0.3)] hover:-translate-y-0.5"
            >
              <MessageCircle size={20} />
              <span>WhatsApp</span>
            </a>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white px-7 py-2.5 rounded-xl font-bold transition-all duration-500 shadow-md hover:shadow-[0_8px_25px_rgba(30,58,138,0.4)] hover:-translate-y-0.5"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden text-gray-700 hover:text-blue-900 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left text-gray-800 hover:text-blue-700 hover:bg-blue-50/50 font-semibold py-3 px-4 rounded-xl transition-all"
              >
                {item.name}
              </button>
            ))}
            <a
              href={`https://wa.me/${COMPANY_CONFIG.whatsappNumber}?text=${encodeURIComponent(COMPANY_CONFIG.whatsappDefaultMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3.5 text-green-600 border-2 border-green-500 hover:bg-green-50 rounded-xl font-bold transition-all duration-300 mt-2"
            >
              <MessageCircle size={20} />
              <span>WhatsApp Us</span>
            </a>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-blue-900 to-blue-700 text-white w-full py-6 rounded-xl font-bold shadow-md"
            >
              Get Quote
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
